import Tool from '../Tool';
import subpixel from '../util/canvasSubpixelOffset';

/** Text tool */
class TextTool extends Tool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		super(core, settings);

		/** @type {number} */
		this.offsetX = 10;
		/** @type {number} */
		this.offsetY = 4;
		/** @type {number} */
		this.TOLERANCE = 0; // Adds margin to hitbox for isWithin
		/** @type {number} */
		this.yValue = 0; // Makes height of box dynamic across methods
	}

	/**
	 * @return {object} Object containing default tool parameters
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {string} value - Text to display in text box
	 * @property {object} style - Text box styles
	 */
	_getDefaultParams() {
		return {
			id: 'text',
			name: 'Text',
			value: '',
			style: this.core.getStyle('tool.text')
		};
	}

	/**
	 * Return a collection of words with additional metadata about each word (break,
	 * value, and whether it is followed by a word boundary).
	 *
	 * Long words (that don't fit on their own line with the current width) are split
	 * here into individual characters, and tagged to not receive a trailing
	 * word boundary when being rendered. This will allow them to wrap in the
	 * container while mid-word.
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @return {string[]} Adjusted array of words to render in text box
	 */
	getParsedWords(ctx) {
		const self = this;
		let chars = [];
		let wordsOut = [];
		const words = this._parseValue();

		// build up new output array
		words.forEach(word => {
			if (!word.length) return;

			// trim trailing spaces
			word = word.replace(/^\s+/, '').replace(/\s+$/, '');

			if (word === '<br/>') {
				// support breaks
				wordsOut.push({
					break: true
				});
			} else if (
				ctx.measureText(word).width + self.offsetX * 2 >
				self.params.style.boxWidth
			) {
				// convert long words to characters with no boundary
				chars = word.split('').map((char, index, mappedChars) => {
					return {
						value: char,
						boundary: index === mappedChars.length - 1 // final character should be followed by a word boundary
					};
				});

				// add characters
				wordsOut = wordsOut.concat(chars);
			} else {
				// normal words
				wordsOut.push({
					value: word,
					boundary: true
				});
			}
		});
		return wordsOut;
	}

	/**
	 * Define hitbox to be full rectangle of text tool bounding box plus tolerance
	 * @param {number[]} mousePanel - Array of mouse coordinates
	 */
	isWithin(mousePanel) {
		if (this.handle.length === 2) {
			const handle1Coords = this.handle[0].getCoords();
			const handle2Coords = this.handle[1].getCoords();

			// Fail if handles aren't defined
			if (!handle1Coords || !handle2Coords) {
				return false;
			}

			// Define tolerance area for text box
			const r1 = {
				x: Math.min(handle1Coords[0], handle2Coords[0]) - this.TOLERANCE,
				y: Math.min(handle1Coords[1], handle2Coords[1]) - this.TOLERANCE,
				width: Math.abs(handle1Coords[0] - handle2Coords[0]) + 2 * this.TOLERANCE,
				height: Math.abs(handle1Coords[1] - handle2Coords[1]) + 2 * this.TOLERANCE
			};

			// Define 1x1 rectangle at mouse location
			const r2 = {
				x: mousePanel[0],
				y: mousePanel[1],
				width: 1,
				height: 1
			};

			return this.intersect(r1, r2);
		}

		// Fail if nothing works
		return false;
	}

	/**
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onClickCanvas(mouse) {
		if (!this.handle.length) {
			const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// first click - create first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle
			this.handle[1] = this.createHandle([
				mousePanel[0] + this.params.style.boxWidth,
				mousePanel[1] + this.params.style.boxHeight
			]);
			this.handle[1].state.visible = true;

			this.handle[1].params.date = null;
			this.handle[1].params.value = null;
			this.handle[1].params.pairedHandle = 0;
			this.handle[1].params.pairedOffsetX = this.params.style.boxWidth;
			this.handle[1].params.pairedOffsetY = this.params.style.boxHeight;

		}

		this.state.inProgressHandle = true;
		this.onComplete();
	}

	/**
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onMousemove(mouse) {
		this.core.onToolMousemove(this);

		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		const idx = Math.round(this.panel.xAxis.primaryScale.invert(mousePanel[0]));
		const isLeft = this.state.dragHandle === this.handle[0];
		const isRight = this.state.dragHandle === this.handle[1];

		if (isLeft) {
			// dragging nw handle

			if (this.panel.core.exchangeDates[idx]) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
				this.state.dragHandle.params.date = this.panel.core.exchangeDates[
					idx
				];
			}
		} else if (isRight) {
			// dragging se handle

			const pairedCoords = this.handle[
				this.state.dragHandle.params.pairedHandle
			].getCoords();

			if (!pairedCoords) return;

			this.state.dragHandle.params.pairedOffsetX =
				mousePanel[0] - pairedCoords[0];
			this.state.dragHandle.params.pairedOffsetY =
				mousePanel[1] - pairedCoords[1];

			const coords = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];

			this.params.style.boxWidth = Math.round(coords[1][0] - coords[0][0]);
			this.params.style.boxHeight = Math.round(coords[1][1] - coords[0][1]);
		} else {
			// Dragging full tool
			this.dragTool(mousePanel);
		}
	}

	/**
	 * Clean up tool value
	 * @return {string[]} Array of cleaned up words
	 */
	_parseValue() {
		// special chars
		this.params.value = this.params.value
			.replace(/&#39;/g, '\'')
			.replace(/&amp;/g, '&')
			.replace(/&gt;/g, '>')
			.replace(/&lt;/g, '<')
			.replace(/\n/g, ' <br/> ');

		try {
			// CW-490
			this.params.value = decodeURIComponent(this.params.value);
		} catch (e) {
			this.params.value = window.unescape(this.params.value);
		}

		return this.params.value.split(/\s/);
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';
		ctx.font = this.panel.getFontStyle(
			this.params.style.font.weight,
			this.params.style.font.size,
			this.params.style.font.family
		);

		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = this.params.style.background.color;
		ctx.lineWidth = this.params.style.width;

		ctx.beginPath();

		ctx.rect(
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		// Render rectangle background
		ctx.fill();


		// Clip the text we're about to draw to the coords rectangle
		ctx.save();
		ctx.clip();

		// Render the words
		this._renderWords(ctx, coords);

		// Finish the clip
		ctx.restore();

		// Render bounding box
		if (this.state.selected || this.state.hover) {
			this._renderBoundingBox(ctx, coords);
			this.handle[0].render();
			this.handle[1].render();
		}


		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw dashed bounding box around tool
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[]} coords
	 */
	_renderBoundingBox(ctx, coords) {
		const { lineDash } = this.params.style;
		ctx.save();
		ctx.strokeStyle = this.params.style.color;
		if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.beginPath();
		subpixel.rect(
			ctx,
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		ctx.stroke();
		ctx.restore();
	}

	/**
	 * Render parsed words to canvas
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number[]} coords - Array of text box handle coordinates
	 * @param {string} value - Text to render in text box
	 */
	_renderWords(ctx, coords, value) {
		const words = this.getParsedWords(ctx, value);
		const self = this;
		let width = 0;
		let x = 0;
		let y = 0;

		ctx.save();
		ctx.fillStyle = this.params.style.text.color;

		// render words
		words.forEach(word => {
			// crlf for breaks
			if (word.break) {
				x = 0;
				y++;
				return;
			}

			// measure word with or without trailing space
			if (word.value) {
				if (word.boundary === false) {
					width = ctx.measureText(word.value).width;
				} else {
					width = ctx.measureText(`${word.value} `).width;
				}
			}

			// crlf if we collide with edge
			if (x + width + self.offsetX > self.params.style.boxWidth) {
				x = 0;
				y++;
				this.yValue = y;
			}

			// draw
			ctx.fillText(
				word.value,
				coords[0][0] + self.offsetX + x,
				coords[0][1] +
					self.offsetY +
					(
						y *
						self.params.style.text.lineHeight *
						self.params.style.font.size
					)
			);

			x += width;
		});
		ctx.restore();
	}

	shrinkToText() {
		const ctx = this.panel.rootToolContext;
		if (ctx.measureText(this.params.value).width < 180) {
			this.handle[1].params.pairedOffsetX = (ctx.measureText(this.params.value).width * 1.4) + 20;
			this.params.style.boxWidth = this.handle[1].params.pairedOffsetX;
			this.handle[1].params.pairedOffsetY = this.params.style.text.lineHeight * this.params.style.font.size;
			this.params.style.boxHeight = this.handle[1].params.pairedOffsetY;
		} else {
			this.handle[1].params.pairedOffsetY = this.params.style.text.lineHeight * this.params.style.font.size * (this.yValue + 1);
			this.params.style.boxHeight = this.handle[1].params.pairedOffsetY;
			this.yValue = 0;
		}
	}

	get shouldSnap() { return false; }

	toJSON() {
		const json = super.toJSON();
		json.params.value = encodeURIComponent(this.params.value);

		return json;
	}
}

export default TextTool;
